import { TIME_FORMAT } from '@/config';
import { phoneNumberInput } from '@/constants/form_inputs';
import {
  domesticSections,
  IDomesticIncidentReport,
  IDomesticSectionKey,
} from '@/models/domestic.incident.report';

import {
  affectedPartiesList,
  commonOffensesList,
  emotionsList,
  languagesList,
  otherEvidenceList,
  physicalEffectsList,
  relationshipsList,
} from './select_data';

const domesticGenderInput = {
  name: 'sex',
  type: 'select',
  label: 'Sex',
  items: [
    {
      label: 'Male',
      value: 'M',
    },
    {
      label: 'Female',
      value: 'F',
    },
    {
      label: 'Self identified',
      value: 'S',
    },
  ],
};
export const DomesticIncidentReportTabs = {
  incident: 'Incident',
  victim: 'Victim (P1)',
  suspect_p2: 'Suspect(P2)',
  victim_interview: 'Victim Interview',
  suspect: 'Suspect',
  witnesses: 'Witnesses ',
  incident_p2: 'Incident',
  evidence: 'Evidence ',
  offense: 'Offense',
  prior_history: 'Prior History',
  closing_notes: 'Closing Notes',
};

export type DomesticTypeKey = keyof typeof DomesticIncidentReportTabs;

export interface ITabSection {
  key: string;
  items: any[];
  title?: string;
  type?: 'multipleCards';
  cards?: any[];
}

const addressHelper = (sectionName: IDomesticSectionKey, addressWidth = 9) => {
  return [
    {
      name: `${sectionName}.customAddress`,
      label: 'Set custom address',
      type: 'checkbox',
      grid: 2,
      value: false,
    },
    {
      label: 'Address',
      name: `${sectionName}.address`,
      type: 'address',
      grid: { md: addressWidth },
      shouldDisplayCallback: (formValues: {
        [x: string]: { customAddress: boolean };
      }) => {
        return !formValues[sectionName]?.customAddress;
      },
    },
    {
      label: 'Address',
      name: `${sectionName}.customAddressStreet`,
      shouldDisplayCallback: (formValues: {
        [x: string]: { customAddress: boolean };
      }) => {
        return formValues[sectionName]?.customAddress === true;
      },
      grid: { md: Math.ceil(addressWidth / 2) },
    },
    {
      label: 'City, State, Zip',
      name: `${sectionName}.customAddressCityStateZip`,
      shouldDisplayCallback: (formValues: {
        [x: string]: { customAddress: boolean };
      }) => {
        return formValues[sectionName]?.customAddress === true;
      },
      grid: { md: Math.floor(addressWidth / 2) },
    },
  ];
};

export const allTabsSections = [
  {
    key: 'incident',
    items: [
      {
        label: 'Agency',
        name: 'incident.agency',
        grid: { md: 6 },
      },
      {
        label: 'ORI',
        name: 'incident.oriNumber',
        grid: { md: 3 },
      },
      {
        label: 'Incident #',
        name: 'incident.incident',
        grid: { md: 3 },
      },
      {
        label: 'Reported Date',
        name: 'incident.reported_date',
        type: 'date',
        grid: { md: 3 },
      },
      {
        label: 'Time (24 hours)',
        name: 'incident.reported_time',
        type: 'time',
        ampm: false,
        grid: { md: 3 },
        timeSteps: { hours: 1, minutes: 1 },
        views: ['hours', 'minutes'],
        format: TIME_FORMAT,
      },
      {
        label: 'Occured Date',
        name: 'incident.occured_date',
        type: 'date',
        grid: { md: 3 },
      },
      {
        label: 'Time (24 hours)',
        name: 'incident.occured_time',
        type: 'time',
        ampm: false,
        grid: { md: 3 },
        timeSteps: { hours: 1, minutes: 1 },
        views: ['hours', 'minutes'],
        format: TIME_FORMAT,
      },
      {
        label: 'Initiated',
        name: 'incident.initiated_check',
        type: 'select',
        defaultValue: [''],
        grid: { md: 12 },
        multiple: true,
        items: [
          {
            label: 'Officer initiated',
            value: 'officer_initiated',
          },
          {
            label: 'Radio run',
            value: 'radio_run',
          },
          {
            label: 'Walk in',
            value: 'walk_in',
          },
          {
            label: 'ICAD (NYC)',
            value: 'ICAD',
          },
        ],
      },
      {
        label: 'Complaint #',
        name: 'incident.complaint',
        grid: { md: 12 },
      },
      ...addressHelper(domesticSections.incident),
    ],
  },
  {
    key: 'victim',
    items: [
      {
        label: 'Victim Name (Last, First, M.I.)',
        name: 'victim.victim_name',
        type: 'select',
        items: 'victimsList',
        grid: { md: 3 },
      },
      {
        label: 'DOB',
        name: 'victim.dob',
        type: 'date',
        grid: { md: 3 },
      },
      {
        label: 'Age',
        name: 'victim.age',
        type: 'number',
        grid: { md: 3 },
      },
      {
        ...domesticGenderInput,
        name: 'victim.gender',
        grid: { md: 3 },
      },
      ...addressHelper(domesticSections.victim, 6),
      {
        label: 'Language',
        name: 'victim.language',
        type: 'select',
        items: languagesList,
        grid: { md: 3 },
      },
      {
        label: 'How can we contact you?',
        name: 'victim.how_to_contact',
        grid: { md: 12 },
      },
    ],
  },
  {
    key: 'suspect_p2',
    items: [
      {
        label: 'Suspect Name (Last, First, M.I.)',
        name: 'suspect_p2.suspect_p2_name',
        type: 'select',
        items: 'offendersList',
      },
      {
        label: 'DOB',
        type: 'date',
        name: 'suspect_p2.dob',
      },
      {
        label: 'Age',
        name: 'suspect_p2.age',
      },
      {
        ...domesticGenderInput,
        name: 'suspect_p2.gender',
      },
      ...addressHelper(domesticSections.suspect_p2, 6),
      {
        label: 'Language',
        name: 'suspect_p2.language',
        type: 'select',
        items: languagesList,
        grid: { md: 3 },
      },

      {
        label: 'Do suspect and victim live together',
        name: 'suspect_p2.live_together',
        grid: { md: 12 },
        type: 'checkbox',
        value: false,
      },
      {
        label: 'Suspect/P2 present ',
        name: 'suspect_p2.suspect_present',
        grid: { md: 12 },
        type: 'checkbox',
        value: false,
      },

      {
        label: 'Was suspect injured? ',
        name: 'suspect_p2.was_suspect_injured',
        grid: { md: 12 },
        type: 'checkbox',
        value: false,
        subItems: [
          {
            isVisible: (itemVal: any) => {
              return itemVal === true;
            },
            name: 'suspect_p2.injury_description',
            grid: { md: 12 },
            placeholder: 'Please enter injury details',
          },
        ],
      },
      {
        label: 'Possible drug or alcohol use?',
        name: 'suspect_p2.possible_substance_use',
        type: 'checkbox',
        value: false,
        grid: { md: 12 },
      },
      {
        label: 'Suspect supervised?',
        name: 'suspect_p2.suspect_supervised_check',
        type: 'select',
        defaultValue: [''],
        multiple: true,
        items: [
          {
            value: 'probation',
            label: 'Probation',
          },
          {
            value: 'parole',
            label: 'Parole',
          },
          {
            value: 'not_supervised',
            label: 'Not Supervised',
          },
          {
            value: 'status_unknown',
            label: 'Status Unknown',
          },
        ],
        grid: { md: 12 },
      },
      {
        label: 'Suspect (P2) Relationship to Victim (P1)',
        name: 'suspect_p2.suspect_P2_relationship_victim_P1_check',
        items: relationshipsList,
        multiple: true,
        grid: { md: 12 },
        type: 'select',
        defaultValue: [''],
        subItems: [
          {
            isVisible: (itemVal: any) => {
              return itemVal?.includes('relative');
            },
            name: 'suspect_p2.relative_details',
            grid: { md: 12 },
            placeholder: 'Please enter relative details',
          },
          {
            isVisible: (itemVal: any) => {
              return itemVal?.includes('other');
            },
            name: 'suspect_p2.other_details',
            grid: { md: 12 },
            placeholder: 'Please enter other details',
          },
        ],
      },
    ],
  },
  {
    key: 'victim_interview',
    items: [
      {
        label: 'Emotional condition of VICTIM?',
        name: 'victim_interview.emo_condition_check',
        type: 'select',
        items: emotionsList,
        multiple: true,
        defaultValue: [''],
        grid: { md: 12 },
        subItems: [
          {
            isVisible: (itemVal: any) => {
              return itemVal?.includes('other');
            },
            name: 'victim_interview.emo_details',
            grid: { md: 12 },
            placeholder: 'Please enter details',
          },
        ],
      },
      {
        label:
          'What were the first words that VICTIM said to the Responding Officers at the scene?',
        name: 'victim_interview.first_words',
        type: 'text-area',
        grid: { md: 12 },
        rows: 10,
        height: 300,
      },
      {
        label: 'Did the suspect make the victim fearful?',
        name: 'victim_interview.fearfull_victim',
        type: 'checkbox',
        value: false,
        grid: { md: 12 },
        subItems: [
          {
            isVisible: (itemVal: any) => {
              return itemVal === true;
            },
            name: 'victim_interview.fearfull_details',
            grid: { md: 12 },
            placeholder: 'Please Describe',
          },
        ],
      },
      {
        label: 'Weapon Used',
        name: 'victim_interview.weapon_used',
        grid: { md: 12 },
        type: 'checkbox',
        value: false,
      },
      {
        label: 'Gun',
        name: 'victim_interview.gun_check',
        grid: { md: 12 },
        type: 'select',
        defaultValue: 'no',
        items: [
          {
            value: 'yes',
            label: 'Yes',
          },
          {
            value: 'no',
            label: 'No',
          },
          {
            value: 'other',
            label: 'Other',
          },
        ],
        subItems: [
          {
            isVisible: (itemVal: any) => {
              return itemVal === 'other';
            },
            name: 'victim_interview.gun_details',
            grid: { md: 12 },
            placehoder: 'Gun details',
          },
        ],
      },

      {
        label: 'Access to guns',
        name: 'victim_interview.access_to_guns',
        grid: { md: 12 },
        type: 'checkbox',
        value: false,
        subItems: [
          {
            isVisible: (itemVal: any) => {
              return itemVal === true;
            },
            name: 'victim_interview.access_to_guns_details',
            grid: { md: 12 },
            placeholder: 'Access information',
          },
        ],
      },
      {
        label: 'Injured',
        name: 'victim_interview.injured',
        grid: { md: 12 },
        type: 'checkbox',
        value: false,
        subItems: [
          {
            isVisible: (itemVal: any) => {
              return itemVal === true;
            },
            name: 'victim_interview.injured_details',
            grid: { md: 12 },
            placeholder: 'Injury details',
          },
        ],
      },
      {
        label: 'In Pain ',
        name: 'victim_interview.in_pain',
        grid: { md: 12 },
        type: 'checkbox',
        value: false,
        subItems: [
          {
            isVisible: (itemVal: any) => {
              return itemVal === true;
            },
            name: 'victim_interview.in_pain_details',
            grid: { md: 12 },
            placeholder: 'Pain description',
          },
        ],
      },

      {
        label: 'Suspect Threats to',
        name: 'victim_interview.suspect_threats_to_check',
        type: 'select',
        items: affectedPartiesList,
        multiple: true,
        defaultValue: [''],
        grid: { md: 12 },
        subItems: [
          {
            isVisible: (itemVal: any) => {
              return itemVal?.includes('other');
            },
            name: 'victim_interview.suspect_threats_to_details',
            grid: { md: 12 },
            placeholder: 'Threats',
          },
        ],
      },
      {
        label: 'Strangulation',
        name: 'victim_interview.strangulation_check',
        type: 'select',
        items: physicalEffectsList,
        multiple: true,
        defaultValue: [''],
        grid: { md: 12 },
      },
      {
        label: 'Visible Marks',
        name: 'victim_interview.visible_marks',
        grid: { md: 12 },
        type: 'checkbox',
        value: false,
        subItems: [
          {
            isVisible: (itemVal: any) => {
              return itemVal === true;
            },
            name: 'victim_interview.visible_marks_details',
            grid: { md: 12 },
            placeholder: 'Marks details',
          },
        ],
      },
    ],
  },
  {
    key: 'suspect',
    items: [
      {
        label: 'What did the suspect say (Before and After Arrest)',
        name: 'suspect.suspect_said',
        type: 'text-area',
        grid: { md: 12 },
      },
      {
        label: '710.30 completed',
        name: 'suspect.completed_710',
        type: 'checkbox',
        value: false,
        grid: { md: 12 },
      },
    ],
  },
  {
    key: 'witnesses',
    type: 'multipleCards',
    cards: [
      {
        title: 'Child/Witness #1',
        type: 'card',
        key: 'witnesses_1',
        items: [
          {
            name: 'witnesses_1.witnesses_1_name',
            label: 'Child/Witness Name',
            type: 'select',
            items: 'witnessesList',
            grid: { md: 8 },
          },
          {
            name: 'witnesses_1.dob',
            label: 'DOB',
            type: 'date',
            grid: { md: 4 },
          },
          ...addressHelper(domesticSections.witnesses_1, 6),
          {
            ...phoneNumberInput,
            name: 'witnesses_1.phone',
            grid: { md: 3 },
          },
        ],
      },
      {
        title: 'Child/Witness #2',
        type: 'card',
        key: 'witnesses_2',
        items: [
          {
            name: 'witnesses_2.witnesses_2_name',
            label: 'Child/Witness Name',
            type: 'select',
            items: 'witnessesList',
            grid: { md: 8 },
          },
          {
            name: 'witnesses_2.dob',
            label: 'DOB',
            type: 'date',
            grid: { md: 4 },
          },
          ...addressHelper(domesticSections.witnesses_2, 6),
          {
            ...phoneNumberInput,
            name: 'witnesses_2.phone',
            grid: { md: 3 },
          },
        ],
      },
      {
        title: 'Child/Witness #3',
        type: 'card',
        key: 'witnesses_3',
        items: [
          {
            name: 'witnesses_3.witnesses_3_name',
            label: 'Child/Witness Name',
            type: 'select',
            items: 'witnessesList',
            grid: { md: 8 },
          },
          {
            name: 'witnesses_3.dob',
            label: 'DOB',
            type: 'date',
            grid: { md: 4 },
          },
          ...addressHelper(domesticSections.witnesses_3, 6),
          {
            ...phoneNumberInput,
            name: 'witnesses_3.phone',
            grid: { md: 3 },
          },
        ],
      },
    ],
  },
  {
    key: 'incident_p2',
    items: [
      {
        name: 'incident_p2.describe_circumstances',
        label: 'Briefly Describe the circumstances of this incident:',
        type: 'text-area',
        grid: { md: 12 },
      },
      {
        name: 'incident_p2.dir_repository_checked',
        label: 'DIR Repository checked?',
        type: 'checkbox',
        value: false,
        grid: { md: 12 },
      },
      {
        name: 'incident_p2.registry_checked',
        label: 'Order of Protection Registry checked?',
        type: 'checkbox',
        value: false,
        grid: { md: 12 },
      },
      {
        name: 'incident_p2.in_effect',
        label: 'Order of Protection in effect?',
        type: 'checkbox',
        value: false,
        grid: { md: 12 },
      },
    ],
  },
  {
    key: 'evidence',
    items: [
      {
        label: 'Photos Taken',
        name: 'evidence.photos_taken_check',
        type: 'select',
        items: [
          { value: 'victim_injury', label: 'Victim injury' },
          { value: 'suspect_injury', label: 'Suspect injury' },
          { value: 'other', label: 'Other' },
        ],
        multiple: true,
        defaultValue: [''],
        grid: { md: 12 },
        subItems: [
          {
            isVisible: (itemVal: any) => {
              return itemVal?.includes('other');
            },
            name: 'evidence.photos_taken_details',
            grid: { md: 12 },
            placeholder: 'Describe',
          },
        ],
      },
      {
        label: 'Other Evidence',
        name: 'evidence.other_evidence_check',
        type: 'select',
        items: otherEvidenceList,
        multiple: true,
        defaultValue: [''],
        grid: { md: 12 },
        subItems: [
          {
            isVisible: (itemVal: any) => {
              return itemVal?.includes('other');
            },
            name: 'evidence.other_evidence_details',
            grid: { md: 12 },
            placeholder: 'Describe',
          },
        ],
      },
      {
        label: 'Destruction of property',
        name: 'evidence.destruction_of_property',
        type: 'checkbox',
        grid: { md: 12 },
        value: false,
        subItems: [
          {
            isVisible: (itemVal: any) => itemVal,
            name: 'evidence.destruction_of_property_details',
            grid: { md: 12 },
            placeholder: 'Please enter details',
          },
        ],
      },
    ],
  },
  {
    key: 'offense',
    items: [
      {
        label: 'Offense Committed',
        name: 'offense.offense_committed',
        type: 'checkbox',
        grid: { md: 12 },
        value: false,
      },
      {
        label: 'Was the suspect arrested?',
        name: 'offense.was_arrested',
        type: 'checkbox',
        value: false,
        grid: { md: 12 },
        subItems: [
          {
            isVisible: (itemVal: any) => !itemVal,
            name: 'offense.was_arrested_details',
            grid: { md: 12 },
            placeholder: 'Please enter details',
          },
        ],
      },
      {
        label: 'offense 1',
        name: 'offense.offense_1',
        type: 'autocomplete',
        clearable: true,
        items: commonOffensesList,
        grid: { md: 12 },
      },
      {
        label: 'offense 2',
        name: 'offense.offense_2',
        type: 'autocomplete',
        clearable: true,
        items: commonOffensesList,
        grid: { md: 12 },
      },
      {
        label: 'offense 3',
        name: 'offense.offense_3',
        type: 'autocomplete',
        clearable: true,
        items: commonOffensesList,
        grid: { md: 12 },
      },
    ],
  },
  {
    key: 'prior_history',
    items: [
      {
        label: 'Describe Victim’s prior domestic incidents with this suspect',
        name: 'prior_history.prior_incidents',
        type: 'text-area',
        grid: { md: 12 },
      },
      {
        label: 'Has Suspect ever (section title)',
        name: '',
        type: 'subtitle',
        grid: { md: 12 },
      },

      {
        label: 'Threatened to kill you or your children',
        name: 'prior_history.threatened_to_kill_child',
        type: 'checkbox',
        grid: { md: 12 },
        value: false,
      },
      {
        label: 'Strangled or “choked” you ',
        name: 'prior_history.strangled_or_choked',
        type: 'checkbox',
        grid: { md: 12 },
        value: false,
      },

      {
        label: 'Beaten you while you were pregnant',
        name: 'prior_history.beaten_while_pregnant',
        type: 'checkbox',
        grid: { md: 12 },
        value: false,
      },

      {
        label: 'Is suspect capable of killing you or children',
        name: 'prior_history.capable_of_killing_child',
        type: 'checkbox',
        grid: { md: 12 },
        value: false,
      },

      {
        label: 'Is the suspect violently and constantly jealous of you?',
        name: 'prior_history.violently_jealous',
        type: 'checkbox',
        grid: { md: 12 },
        value: false,
      },

      {
        label:
          'Has the physical violence increased in frequency or severity over the past 6 months?',
        name: 'prior_history.increased_severity_past_months',
        type: 'checkbox',
        grid: { md: 12 },
        value: false,
      },
      {
        label:
          'Refer to the NYS Domestic and Sexual Violence Hotline at 1 -800-942-6906 or Local Domestic Violence Service Provider: ()__-___',
        name: ' ',
        type: 'alert-info',
        grid: { md: 12 },
        shouldDisplayCallback: (formValues: IDomesticIncidentReport) => {
          return [
            formValues.prior_history?.threatened_to_kill_child,
            formValues.prior_history?.strangled_or_choked,
            formValues.prior_history?.beaten_while_pregnant,
            formValues.prior_history?.capable_of_killing_child,
            formValues.prior_history?.violently_jealous,
            formValues.prior_history?.increased_severity_past_months,
          ].some((item) => item);
        },
      },
    ],
  },
  {
    key: 'closing_notes',
    items: [
      {
        label:
          'Is there reasonable cause to suspect a child may be the victim of abuse, neglect, maltreatment or endangerment? ',
        name: 'closing_notes.child_victim_neglect',
        type: 'checkbox',
        grid: { md: 12 },
        value: false,
        subItems: [
          {
            isVisible: (itemVal: any) => itemVal,
            name: 'closing_notes.child_victim_details',
            grid: { md: 12 },
            type: 'alert-info',
            label:
              'the Officer must contact the NYS Child Abuse Hotline Registry # 1-800-635-1522',
          },
        ],
      },
      {
        label: 'Was DIR given to the Victim at the scene?',
        name: 'closing_notes.is_dir_given',
        type: 'checkbox',
        value: false,
        grid: { md: 12 },
        subItems: [
          {
            isVisible: (itemVal: any) => !itemVal,
            name: 'closing_notes.is_dir_given_details',
            grid: { md: 12 },
            placeholder: 'Please explain',
          },
        ],
      },
      {
        label: 'Was Victim Rights Notice given to the Victim?',
        name: 'closing_notes.is_rights_given',
        type: 'checkbox',
        value: false,
        grid: { md: 12 },
        subItems: [
          {
            isVisible: (itemVal: any) => !itemVal,
            name: 'closing_notes.is_rights_given_details',
            grid: { md: 12 },
            placeholder: 'Please explain',
          },
        ],
      },
    ],
  },
];
